import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Paper,
  Chip, CircularProgress, Alert, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox
} from '@mui/material';
import {
  Assignment, People, Engineering, Folder, Business,
  AttachMoney, CalendarToday, TrendingUp, Assessment,
  Add, Visibility, Edit, FileDownload, Refresh, Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService, expedientesApi, clientesApi, tecnicosApi } from '../services/api';
import { Cliente, Tecnico } from '../types/expediente';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  total_expedientes: number;
  activos: number;
  cobrados: number;
  importe_total: number;
  total_usuarios: number;
  total_clientes: number;
  total_tecnicos_activos: number;
  total_documentos: number;
}

interface ExpedienteCreate {
  codigo_actual?: string;
  codigo_original?: string;
  numero?: number;
  anio?: number;
  tipo?: string;
  nig?: string;
  cliente_id?: string;  // Cambiado de juzgado_id a cliente_id
  tecnico_id?: string;
  observaciones_generales?: string;
  estado?: string;
  es_activo?: boolean;
  justicia_gratuita?: boolean;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAbogado, user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados del modal
  const [modalOpen, setModalOpen] = useState(false);
  const [creatingExpediente, setCreatingExpediente] = useState(false);
  const [loadingCodigoActual, setLoadingCodigoActual] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);

  // Estados del formulario
  const [formData, setFormData] = useState<ExpedienteCreate>({
    codigo_actual: '', // Se generará automáticamente
    numero: undefined, // Cambiado a undefined para validación correcta
    anio: new Date().getFullYear(), // Año actual por defecto
    tipo: '',
    nig: '',
    cliente_id: '', // Campo obligatorio
    tecnico_id: '',
    observaciones_generales: '',
    estado: 'activo',
    es_activo: true,
    justicia_gratuita: false
  });

  useEffect(() => {
    if (isAbogado) {
      navigate('/minutas', { replace: true });
      return;
    }
    loadStats();
  }, [isAbogado]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllStats();
      setStats(data);
    } catch (err) {
      setError('Error cargando estadísticas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientesYTecnicos = async () => {
    try {
      const [clientesData, tecnicosData] = await Promise.all([
        clientesApi.getClientes(0, 1000),
        tecnicosApi.getTecnicos(0, 1000, true)
      ]);
      setClientes(clientesData);
      setTecnicos(tecnicosData);
    } catch (err) {
      console.error('Error cargando clientes y técnicos:', err);
    }
  };

  // Función para obtener el siguiente código actual disponible
  const obtenerSiguienteCodigoActual = async () => {
    try {
      setLoadingCodigoActual(true);
      const expedientes = await expedientesApi.getExpedientes(0, 10000);
      
      // Obtener todos los códigos actuales existentes y convertir a números
      const codigosExistentes = expedientes
        .map((exp: any) => exp.codigo_actual)
        .filter((codigo: any) => codigo && !isNaN(Number(codigo)))
        .map((codigo: any) => Number(codigo))
        .sort((a: number, b: number) => a - b);

      // Encontrar el siguiente número disponible
      let siguienteCodigo = 1;
      for (const codigo of codigosExistentes) {
        if (codigo === siguienteCodigo) {
          siguienteCodigo++;
        } else {
          break;
        }
      }

      setFormData(prev => ({
        ...prev,
        codigo_actual: siguienteCodigo.toString(),
        codigo_original: siguienteCodigo.toString()  // ← AÑADIR ESTA LÍNEA
      }));

    } catch (err) {
      console.error('Error obteniendo siguiente código:', err);
      setError('Error generando código automático');
    } finally {
      setLoadingCodigoActual(false);
    }
  };

  const handleOpenModal = async () => {
    await loadClientesYTecnicos();
    await obtenerSiguienteCodigoActual();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setError(null);
    setFormData({
      codigo_actual: '',
      numero: undefined,
      anio: new Date().getFullYear(),
      tipo: '',
      nig: '',
      cliente_id: '',
      tecnico_id: '',
      observaciones_generales: '',
      estado: 'activo',
      es_activo: true,
      justicia_gratuita: false
    });
  };

  const handleInputChange = (field: keyof ExpedienteCreate, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateExpediente = async () => {
    try {
      setCreatingExpediente(true);
      setError(null);
      
      // Validación de campos obligatorios
      if (!formData.tipo || !formData.numero || !formData.anio || !formData.cliente_id) {
        setError('Tipo, Número, Año y Cliente son campos obligatorios');
        return;
      }

      const nuevoExpediente = await expedientesApi.createExpediente(formData as any);
      
      handleCloseModal();
      navigate(`/expedientes/${nuevoExpediente.id}`);
      
    } catch (err: any) {
      setError(err.message || 'Error creando expediente');
    } finally {
      setCreatingExpediente(false);
    }
  };

  // Validación para habilitar el botón de crear
  const isFormValid = () => {
    return !!(
      formData.tipo && 
      formData.numero && 
      formData.anio && 
      formData.cliente_id &&
      formData.codigo_actual
    );
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return '0€';
    return `${amount.toLocaleString('es-ES', { minimumFractionDigits: 2 })}€`;
  };

  // Opciones para el campo tipo
  const tipoOptions = [
    { value: 'ORD', label: 'ORD - Procedimiento Ordinario' },
    { value: 'DP', label: 'DP - Diligencias Previas' },
    { value: 'DPA', label: 'DPA - Diligencias Previas Abreviadas' },
    { value: 'DUD', label: 'DUD - Diligencias Urgentes/Juicio Rápido' },
    { value: 'PA', label: 'PA - Procedimiento Abreviado' },
    { value: 'EJ', label: 'EJ - Ejecutoria' },
    { value: 'ENJ', label: 'ENJ - ENJ' },
    { value: 'ETJ', label: 'ETJ - ETJ' },
    { value: 'JVB', label: 'JVB - Juicio Verbal' },
    { value: 'LEV', label: 'LEV - Leve' },
    { value: 'RAP', label: 'RAP - Recurso de Apelación' },
    { value: 'TJ', label: 'TJ - TJ' }
  ];

  // Datos de los módulos principales (sin cambios)
  const modulos = [
    {
      title: 'Expedientes',
      icon: <Assignment sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      bgColor: '#e3f2fd',
      description: 'Gestión completa de expedientes periciales',
      count: stats?.total_expedientes || 0,
      countLabel: 'Total expedientes',
      actions: [
        { label: 'Ver Listado', action: () => navigate('/expedientes'), icon: <Visibility /> },
        { label: 'Nuevo Expediente', action: handleOpenModal, icon: <Add /> },
        { label: 'Buscar', action: () => navigate('/expedientes?search=true'), icon: <Assignment /> }
      ]
    },
    {
      title: 'Usuarios',
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      bgColor: '#f3e5f5',
      description: 'Gestión de usuarios del sistema',
      count: stats?.total_usuarios || 0,
      countLabel: 'Usuarios registrados',
      actions: [
        { label: 'Ver Usuarios', action: () => navigate('/usuarios'), icon: <Visibility /> },
        { label: 'Nuevo Usuario', action: () => navigate('/usuarios/nuevo'), icon: <Add /> },
        { label: 'Configuración', action: () => navigate('/usuarios/config'), icon: <People /> }
      ]
    },
    {
      title: 'Clientes',
      icon: <Business sx={{ fontSize: 40 }} />,
      color: '#388e3c',
      bgColor: '#e8f5e8',
      description: 'Gestión de clientes y juzgados',
      count: stats?.total_clientes || 0,
      countLabel: 'Clientes registrados',
      actions: [
        { label: 'Ver Clientes', action: () => navigate('/clientes'), icon: <Visibility /> },
        { label: 'Nuevo Cliente', action: () => navigate('/clientes/nuevo'), icon: <Add /> },
        { label: 'Juzgados', action: () => navigate('/juzgados'), icon: <Business /> }
      ]
    },
    {
      title: 'Técnicos',
      icon: <Engineering sx={{ fontSize: 40 }} />,
      color: '#f57c00',
      bgColor: '#fff3e0',
      description: 'Gestión de técnicos y peritos',
      count: stats?.total_tecnicos_activos || 0,
      countLabel: 'Técnicos activos',
      actions: [
        { label: 'Ver Técnicos', action: () => navigate('/tecnicos'), icon: <Visibility /> },
        { label: 'Nuevo Técnico', action: () => navigate('/tecnicos/nuevo'), icon: <Add /> },
        { label: 'Especialidades', action: () => navigate('/especialidades'), icon: <Engineering /> }
      ]
    },
    {
      title: 'Documentos',
      icon: <Folder sx={{ fontSize: 40 }} />,
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
      description: 'Gestión de documentos y archivos',
      count: stats?.total_documentos || 0,
      countLabel: 'Documentos generados',
      actions: [
        { label: 'Ver Documentos', action: () => navigate('/documentos'), icon: <Visibility /> },
        { label: 'Subir Archivo', action: () => navigate('/documentos/upload'), icon: <Add /> },
        { label: 'Plantillas', action: () => navigate('/plantillas'), icon: <FileDownload /> }
      ]
    }
  ];

  // Estadísticas rápidas (sin cambios)
  const estadisticasRapidas = [
    {
      title: 'Ingresos Totales',
      value: formatCurrency(stats?.importe_total || 0),
      icon: <AttachMoney />,
      color: '#4caf50',
      trend: '+12%'
    },
    {
      title: 'Expedientes Activos',
      value: stats?.activos || 0,
      icon: <Assignment />,
      color: '#2196f3',
      trend: '+5%'
    },
    {
      title: 'Cobrados',
      value: stats?.cobrados || 0,
      icon: <TrendingUp />,
      color: '#ff9800',
      trend: '+8%'
    },
    {
      title: 'Pendientes',
      value: (stats?.total_expedientes || 0) - (stats?.cobrados || 0),
      icon: <CalendarToday />,
      color: '#f44336',
      trend: '-3%'
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !modalOpen) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🏠 Dashboard Peritaciones
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Sistema de gestión integral de informes periciales
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <IconButton onClick={loadStats} color="primary">
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Assessment />}
            onClick={() => navigate('/reportes')}
          >
            Reportes
          </Button>
        </Box>
      </Box>

      {/* Estadísticas Rápidas */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        {estadisticasRapidas.map((stat, index) => (
          <Box key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ color: stat.color }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stat.trend} vs mes anterior
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: `${stat.color}20`,
                      borderRadius: '50%',
                      p: 1,
                      color: stat.color
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Módulos Principales */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        📋 Módulos del Sistema
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' }, gap: 4 }}>
        {modulos.map((modulo, index) => (
          <Box key={index}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      backgroundColor: modulo.bgColor,
                      borderRadius: '12px',
                      p: 1,
                      mr: 2,
                      color: modulo.color
                    }}
                  >
                    {modulo.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="div">
                      {modulo.title}
                    </Typography>
                    <Chip
                      label={`${modulo.count} ${modulo.countLabel}`}
                      size="small"
                      sx={{ backgroundColor: modulo.bgColor, color: modulo.color }}
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="textSecondary" mb={3}>
                  {modulo.description}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={1}>
                  {modulo.actions.map((action, actionIndex) => (
                    <Button
                      key={actionIndex}
                      variant={actionIndex === 0 ? "contained" : "outlined"}
                      size="small"
                      startIcon={action.icon}
                      onClick={action.action}
                      sx={{
                        backgroundColor: actionIndex === 0 ? modulo.color : 'transparent',
                        borderColor: actionIndex === 0 ? modulo.color : modulo.color,
                        color: actionIndex === 0 ? 'white' : modulo.color,
                        '&:hover': {
                          backgroundColor: actionIndex === 0 ? modulo.color : `${modulo.color}10`,
                        }
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Modal mejorado para crear expediente */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">📋 Crear Nuevo Expediente</Typography>
            <IconButton onClick={handleCloseModal}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loadingCodigoActual && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="textSecondary">
                Generando código automático...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, pt: 2 }}>
            
            {/* Código Actual - Solo lectura */}
            <TextField
              fullWidth
              label="Código Actual"
              value={formData.codigo_actual}
              disabled
              helperText="Generado automáticamente por el sistema"
              InputProps={{
                startAdornment: formData.codigo_actual ? '🏷️ ' : '⏳ '
              }}
            />

            {/* Tipo - Obligatorio */}
            <FormControl fullWidth required>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo *"
                onChange={(e) => handleInputChange('tipo', e.target.value)}
              >
                <MenuItem value="">
                  <em>Selecciona un tipo</em>
                </MenuItem>
                {tipoOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Número - Obligatorio */}
            <TextField
              fullWidth
              label="Número *"
              type="number"
              value={formData.numero || ''}
              onChange={(e) => handleInputChange('numero', parseInt(e.target.value) || undefined)}
              required
              inputProps={{ min: 1 }}
              helperText="Número del procedimiento"
            />

            {/* Año - Obligatorio */}
            <TextField
              fullWidth
              label="Año *"
              type="number"
              value={formData.anio}
              onChange={(e) => handleInputChange('anio', parseInt(e.target.value) || new Date().getFullYear())}
              required
              inputProps={{ min: 2000, max: 2050 }}
              helperText="Año del procedimiento"
            />

            {/* Cliente - Obligatorio */}
            <FormControl fullWidth required sx={{ gridColumn: { md: 'span 2' } }}>
              <InputLabel>Cliente *</InputLabel>
              <Select
                value={formData.cliente_id}
                label="Cliente *"
                onChange={(e) => handleInputChange('cliente_id', e.target.value)}
              >
                <MenuItem value="">
                  <em>Selecciona un cliente</em>
                </MenuItem>
                {clientes.map(cliente => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    <Box>
                      <Typography variant="body1">{cliente.nombre}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cliente.tipo} {cliente.codigo && `- ${cliente.codigo}`}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* NIG - Opcional */}
            <TextField
              fullWidth
              label="NIG"
              value={formData.nig}
              onChange={(e) => handleInputChange('nig', e.target.value)}
              helperText="Número de Identificación General (opcional)"
            />

            {/* Técnico - Opcional */}
            <FormControl fullWidth>
              <InputLabel>Técnico Asignado</InputLabel>
              <Select
                value={formData.tecnico_id}
                label="Técnico Asignado"
                onChange={(e) => handleInputChange('tecnico_id', e.target.value)}
              >
                <MenuItem value="">
                  <em>Sin asignar</em>
                </MenuItem>
                {tecnicos.map(tecnico => (
                  <MenuItem key={tecnico.id} value={tecnico.id}>
                    <Box>
                      <Typography variant="body1">
                        {tecnico.nombre} {tecnico.apellidos}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {tecnico.especialidad}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Observaciones */}
            <TextField
              fullWidth
              label="Observaciones"
              multiline
              rows={3}
              value={formData.observaciones_generales}
              onChange={(e) => handleInputChange('observaciones_generales', e.target.value)}
              sx={{ gridColumn: { md: 'span 2' } }}
              helperText="Observaciones generales del expediente"
            />

            {/* Checkboxes */}
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.justicia_gratuita}
                    onChange={(e) => handleInputChange('justicia_gratuita', e.target.checked)}
                  />
                }
                label="Justicia Gratuita"
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={creatingExpediente}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateExpediente}
            variant="contained"
            disabled={!isFormValid() || creatingExpediente}
            startIcon={creatingExpediente ? <CircularProgress size={16} /> : <Add />}
          >
            {creatingExpediente ? 'Creando...' : 'Crear Expediente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;